import type {
  CollectionReference,
  DocumentData,
  QueryFilterConstraint,
} from 'firebase/firestore'
import {
  and,
  collection,
  doc,
  limit,
  or,
  orderBy,
  query,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import { getDocsWithError } from '../../firestore-mobx/fetch'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SearchUser } from '../../stores/SearchUser'
import type { FirestoreSearchUser } from './schema'
import { schema } from './schema'
import { documentSnapshots } from '../../firestore-mobx/stream'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSearchUser> = {
  toFirestore: () => {
    throw new Error('Client cannot write FirestoreSearchUser')
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error PublicUser', e.errors)
      }
      throw e
    }
  },
}

const baseCollectionRef = (
  firestore: Firestore
): CollectionReference<FirestoreSearchUser, DocumentData> => {
  return collection(firestore, 'user_search').withConverter(converter)
}

export const fetchSearchedUsers = async (
  repository: FirebaseRepository,
  role: string | string[],
  term?: string
) => {
  const roleArray = role instanceof Array ? role : [role]
  const constraints: QueryFilterConstraint[] = [where('role', 'in', roleArray)]

  const safeTerm = term?.trim().toLowerCase() || ''

  constraints.push(
    or(
      and(
        where('firstName', '>=', safeTerm),
        where('firstName', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('emailAddress', '>=', safeTerm),
        where('emailAddress', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('emailDomain', '>=', safeTerm),
        where('emailDomain', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('lastName', '>=', safeTerm),
        where('lastName', '<', safeTerm + '\uf8ff')
      )
    )
  )

  const q = query(
    baseCollectionRef(repository.firestore),
    and(...constraints),
    term ? limit(100) : limit(1000),
    orderBy('lastName', 'asc')
  )

  // wait for
  const users = await getDocsWithError(q, 'SearchUserError')

  const results: SearchUser[] = users.docs.map((doc) => {
    const data = doc.data()
    return new SearchUser(repository, doc.id, data)
  })

  return results
}

export const getSearchUser = (
  repository: FirebaseRepository,
  userId: string
) => {
  const docRef = doc(baseCollectionRef(repository.firestore), userId)

  return documentSnapshots(docRef).map((snapshot) => {
    if (!snapshot.exists())
      throw new Error(`SearchUser with ID ${userId} does not exist`)
    return new SearchUser(repository, snapshot.id, snapshot.data())
  })
}
